/**
 * Video Management Redux Slice
 * 
 * Centralized state management for video playback and fullscreen functionality.
 * Allows control of video playback from higher-level components (e.g., Main screen)
 * when switching tabs or navigating away.
 * 
 * Architecture note:
 * - videoSlice.ts: Manages video data (decryption keys, URLs, loading state)
 * - videoManagementSlice.ts: Manages playback control (active video, playback state, fullscreen)
 * - useVideoManagement.ts: Orchestrates both slices for high-level operations
 * 
 * Key responsibilities:
 * - Track active video and its playback state
 * - Manage fullscreen mode and video element positioning
 * - Store video element references and original DOM parents
 * - Enable pause/play control from any component level
 * 
 * Design pattern:
 * - Components dispatch actions to update state
 * - Components subscribe to state via selectors
 * - Higher-level components can control playback by dispatching actions
 * - Hooks orchestrate complex flows (e.g., dispose → init) across multiple slices
 */

import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { initializeVideo } from '@/presentation/store/slices/video/videoSlice';
import { videoElementRegistry } from '@/presentation/registries/videoElementRegistry';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';
import { subscribeToTabVisibility } from '@/presentation/utils/tabVisibility';
import { subscribeToBeforeUnload } from '@/infrastructure/beforeUnload';

/**
 * Global reference to the fullscreen container element
 * This is set by the component that renders the fullscreen overlay
 */
let fullscreenContainerElement: HTMLElement | null = null;

export const setFullscreenContainer = (element: HTMLElement | null) => {
    if (element) {
        // console.log('📦 [Redux] Fullscreen container SET', { tagName: element.tagName, id: element.id });
    } else {
        // console.log('📦 [Redux] Fullscreen container CLEARED');
    }
    fullscreenContainerElement = element;
};

// App-global lifecycle listeners: pause all videos when the tab is hidden or
// about to unload. Pausing fires each video's native `pause` event, which is
// the single trigger for downstream side effects (e.g. saveToWatchHistory in
// useContentPlaybackTracker). Attached once at module load.
//
// The tab-visibility branch is delegated to `subscribeToTabVisibility`
// (shared with `RouteStack` and `useChat`). The page-unload branch is
// delegated to `subscribeToBeforeUnload` (the shared infrastructure surface,
// also consumed by `analyticsService` and the last-seen listener). Both
// subscribe* utilities are SSR-defensive on their own (each returns a no-op
// unsubscribe when `window`/`document` is undefined), so no local `window`
// guard is needed here. The returned unsubscribe handles are intentionally
// NOT stored — these module-load listeners live for the whole app session
// (matches the original behavior).
const pauseAllOnLifecycle = () => videoElementRegistry.pauseAll();
subscribeToBeforeUnload(pauseAllOnLifecycle);
subscribeToTabVisibility((isVisible) => {
    if (!isVisible) pauseAllOnLifecycle();
});

/**
 * Async thunk to request browser fullscreen API
 * Must be called synchronously from user gesture (click handler)
 */
export const requestBrowserFullscreen = createAsyncThunk(
    'videoManagement/requestBrowserFullscreen',
    async () => {
        // console.log('🎬 [Redux] requestBrowserFullscreen thunk called');

        if (!fullscreenContainerElement) {
            appLogger.error('Fullscreen container not set!', 'VideoManagementSlice.requestBrowserFullscreen');
            throw new Error('Fullscreen container not set');
        }

        // console.log('🎬 [Redux] Container element found, requesting fullscreen');
        const element = fullscreenContainerElement;

        try {
            // Standard API (modern browsers)
            if (element.requestFullscreen) {
                await element.requestFullscreen();
                // console.log('✅ [Redux] requestFullscreen() successful');
            }
            // Safari/iOS - try container fullscreen first (iOS 16.4+)
            else if ((element as any).webkitRequestFullscreen) {
                // Try with options for better iOS support
                if (typeof (element as any).webkitRequestFullscreen === 'function') {
                    await (element as any).webkitRequestFullscreen();
                    // console.log('✅ [Redux] webkitRequestFullscreen() successful');
                }
            }
            // Older Safari/iOS - try video element fullscreen as fallback
            else if ((element as any).webkitEnterFullscreen) {
                // For video elements specifically on older iOS
                (element as any).webkitEnterFullscreen();
                // console.log('✅ [Redux] webkitEnterFullscreen() successful');
            }
            // Firefox
            else if ((element as any).mozRequestFullScreen) {
                await (element as any).mozRequestFullScreen();
                // console.log('✅ [Redux] mozRequestFullScreen() successful');
            }
            // IE/Edge Legacy
            else if ((element as any).msRequestFullscreen) {
                await (element as any).msRequestFullscreen();
                // console.log('✅ [Redux] msRequestFullscreen() successful');
            }
            else {
                console.warn('⚠️ [Redux] No fullscreen API available on this browser');
            }
        } catch (error) {
            // Browser might block fullscreen - log but don't fail
            console.warn('⚠️ [Redux] Browser fullscreen request failed:', error);
            throw error;
        }
    }
);

/**
 * Async thunk to request native fullscreen on the video element itself
 * Used when WEB_NATIVE_FULLSCREEN_MODE is true — delegates to the browser's
 * native video player fullscreen rather than our CSS overlay.
 * Must be called synchronously from user gesture (click handler).
 */
export const requestVideoElementFullscreen = createAsyncThunk(
    'videoManagement/requestVideoElementFullscreen',
    async ({ videoKey }: { videoKey: string }) => {
        const videoElement = videoElementRegistry.get(videoKey);

        if (!videoElement) {
            console.warn('⚠️ [Redux] Video element not found for key:', videoKey);
            return;
        }

        try {
            // Standard API (modern browsers)
            if (videoElement.requestFullscreen) {
                await videoElement.requestFullscreen();
            }
            // iOS Safari - video element specific fullscreen (most reliable on iOS)
            else if ((videoElement as any).webkitEnterFullscreen) {
                (videoElement as any).webkitEnterFullscreen();
            }
            // Safari desktop
            else if ((videoElement as any).webkitRequestFullscreen) {
                await (videoElement as any).webkitRequestFullscreen();
            }
            // Firefox
            else if ((videoElement as any).mozRequestFullScreen) {
                await (videoElement as any).mozRequestFullScreen();
            }
            // IE/Edge Legacy
            else if ((videoElement as any).msRequestFullscreen) {
                await (videoElement as any).msRequestFullscreen();
            }
            else {
                console.warn('⚠️ [Redux] No fullscreen API available on this browser');
            }
        } catch (error) {
            console.warn('⚠️ [Redux] Video element fullscreen request failed:', error);
            throw error;
        }
    }
);

/**
 * Exit browser fullscreen
 */
export const exitBrowserFullscreen = createAsyncThunk(
    'videoManagement/exitBrowserFullscreen',
    async () => {
        if (document.fullscreenElement || (document as any).webkitFullscreenElement) {
            try {
                if (document.exitFullscreen) {
                    await document.exitFullscreen();
                } else if ((document as any).webkitExitFullscreen) {
                    await (document as any).webkitExitFullscreen();
                } else if ((document as any).mozCancelFullScreen) {
                    await (document as any).mozCancelFullScreen();
                } else if ((document as any).msExitFullscreen) {
                    await (document as any).msExitFullscreen();
                }
            } catch (error) {
                console.warn('[VideoManagement] Error exiting fullscreen:', error);
            }
        }
    }
);

/**
 * Video playback state for a single video
 */
export interface VideoPlaybackState {
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    volume: number;
    isMuted: boolean;
    /** Whether video was forced paused (by manual user interaction or item out of viewport) 
     * vs navigation events (navigating away, which will allow resume when navigating back) */
    forcedPaused: boolean;
}

/**
 * Fullscreen video state
 */
export interface FullscreenVideoState {
    /** Whether fullscreen mode is active */
    isFullscreen: boolean;
    /** Media entity of the video in fullscreen */
    media: UserMediaEntity | null;
    /** Reference to video element (stored as key for serialization) */
    videoElementKey: string | null;
}

/**
 * Video element tracking for DOM manipulation
 * Note: We store keys and use refs in components for actual DOM elements
 * since Redux state must be serializable
 */
export interface VideoElementState {
    /** Video Key (composed from screenKey and mediaId) */
    videoKey: string;
    /** Whether video element is ready */
    isReady: boolean;
    /** Timestamp when video element became ready */
    readyTimestamp: number;
}

export interface VideoManagementState {
    /** Currently active/playing video */
    activeVideoKey: string | null;

    /** Fullscreen state */
    fullscreen: FullscreenVideoState;

    /** Map of video keys to their element states */
    videoElements: Record<string, VideoElementState>;

    /** Map of video keys to their playback states */
    playbackStates: Record<string, VideoPlaybackState>;

    /** Videos pending initialization (clicked but not yet initialized) */
    pendingInitialization: Record<string, { media: UserMediaEntity; autoplay: boolean }>;
}

const initialPlaybackState: VideoPlaybackState = {
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
    isMuted: false,
    forcedPaused: false,
};

const initialState: VideoManagementState = {
    activeVideoKey: null,
    fullscreen: {
        isFullscreen: false,
        media: null,
        videoElementKey: null,
    },
    videoElements: {},
    playbackStates: {},
    pendingInitialization: {},
};

/**
 * Async thunk to initialize and optionally autoplay a video
 * This is the main entry point for video initialization from UI components
 * Note: Disposal of previous videos should be handled by the calling code (e.g., useVideoManagement hook)
 */
export const initVideo = createAsyncThunk(
    'videoManagement/initVideo',
    async (
        { media, videoKey, autoplay = false }
            : { media: UserMediaEntity; videoKey: string; autoplay?: boolean },
        { dispatch }
    ) => {
        // Kill-switches mirroring Flutter video_player_notifier.dart:46-52.
        // When initVideos is false or budgetExceeded is true, skip CDN auth + HLS init
        // entirely so ops can stop CDN spend. UI degrades to thumbnail-only.
        // Read inside the thunk body so the live Remote Config value is picked up per
        // invocation. Flutter returns silently here — no overlay is raised (:50-52).
        if (!remoteConfigService.initVideos || remoteConfigService.budgetExceeded) {
            return { media, videoKey, autoplay: false, result: null };
        }

        // Dispatch the video initialization from video slice
        const result = await dispatch(initializeVideo({
            media,
            videoKey
        }));

        return { media, videoKey, autoplay, result };
    }
);

const videoManagementSlice = createSlice({
    name: 'videoManagement',
    initialState,
    reducers: {
        /**
         * Register a video element as ready
         * Called when video element is mounted and ready to use
         */
        registerVideoElementState: (state, action: PayloadAction<{ videoKey: string }>) => {
            const { videoKey } = action.payload;

            // First check if already registered
            if (state.videoElements[videoKey]?.isReady) {
                return;
            }

            // Register video element state
            state.videoElements[videoKey] = {
                videoKey,
                isReady: true,
                readyTimestamp: Date.now(),
            };

            // Initialize playback state if not exists
            if (!state.playbackStates[videoKey]) {
                state.playbackStates[videoKey] = { ...initialPlaybackState };
            }
        },

        /**
         * Unregister a video element
         * Called when video element is unmounted
         */
        unregisterVideoElementState: (state, action: PayloadAction<{ videoKey: string }>) => {
            const { videoKey } = action.payload;
            delete state.videoElements[videoKey];
            delete state.playbackStates[videoKey];
            delete state.pendingInitialization[videoKey];

            // Commented out: Do not clear active/fullscreen here to allow active video tracking and
            // fullscreen mode persistence even if component unmounts

            // Clear active video if this was it
            if (state.activeVideoKey === videoKey) {
                // console.log(`🗑️ [VideoManagement] Active video ${videoKey} unregistered, clearing activeVideoKey`);
                state.activeVideoKey = null;
            }

            // // Clear fullscreen if this was the fullscreen video
            if (state.fullscreen.videoElementKey === videoKey) {
                state.fullscreen = {
                    isFullscreen: false,
                    media: null,
                    videoElementKey: null,
                };
            }
        },

        /**
         * Update video playback state
         * Called when video playback state changes (play/pause/seek/volume)
         */
        updatePlaybackState: (
            state,
            action: PayloadAction<{ videoKey: string; playbackState: Partial<VideoPlaybackState> }>
        ) => {
            const { videoKey, playbackState } = action.payload;

            if (!state.playbackStates[videoKey]) {
                state.playbackStates[videoKey] = { ...initialPlaybackState };
            }

            state.playbackStates[videoKey] = {
                ...state.playbackStates[videoKey],
                ...playbackState,
            };
        },

        /**
         * Set the active video
         * Called when a video starts playing
         */
        setActiveVideo: (state, action: PayloadAction<{ videoKey: string }>) => {
            // console.log(`🎬 [VideoManagement] Setting active video to ${action.payload.videoKey}`);
            state.activeVideoKey = action.payload.videoKey;
        },

        /**
         * Clear the active video
         * Called when video is paused or stopped
         */
        clearActiveVideo: (state) => {
            // console.log(`🗑️ [VideoManagement] Clearing active video`);
            state.activeVideoKey = null;
        },

        /**
        * Request play for a specific video
        * Can be called from any level to start playback
        * Always clears pausedManually flag when playing
        */
        requestPlay: (state, action: PayloadAction<{ videoKey: string }>) => {
            const { videoKey } = action.payload;

            // Update state - actual play() call happens in component via selector
            if (state.playbackStates[videoKey]) {
                state.playbackStates[videoKey].isPlaying = true;
                state.playbackStates[videoKey].forcedPaused = false;
            }

            // Reset active video to this one (even if initially done on initialization)
            state.activeVideoKey = videoKey;
        },

        /**
         * Request pause for a specific video
         * Can be called from any level to pause playback
         * Note: Does NOT clear activeVideoKey - video remains active even when paused
         * @param manuallyPaused - Whether this is a manual user pause (prevents auto-resume)
         */
        requestPause: (state, action: PayloadAction<{ videoKey: string; forced?: boolean }>) => {
            const { videoKey, forced: manuallyPaused = false } = action.payload;

            // Update state - actual pause() call happens in component via selector
            if (state.playbackStates[videoKey]) {
                state.playbackStates[videoKey].isPlaying = false;
                state.playbackStates[videoKey].forcedPaused = manuallyPaused;
            }

            // Keep video as active even when paused (for resume functionality)
            // Only cleared when a different video becomes active or explicitly cleared
        },

        /**
         * Clear all video management state
         * Called on logout or when leaving video screens
         */
        clearAllVideoManagement: (state) => {
            state.activeVideoKey = null;
            state.fullscreen = {
                isFullscreen: false,
                media: null,
                videoElementKey: null,
            };
            state.videoElements = {};
            state.playbackStates = {};
            state.pendingInitialization = {};
        },

        /**
         * Enter fullscreen mode
         * Called when user clicks fullscreen button
         * Note: Browser fullscreen API must be called separately via requestBrowserFullscreen
         * from the synchronous click handler to preserve user gesture
         */
        enterFullscreen: (
            state,
            action: PayloadAction<{ media: UserMediaEntity; videoKey: string }>
        ) => {
            const { media, videoKey } = action.payload;

            state.fullscreen = {
                isFullscreen: true,
                media,
                videoElementKey: videoKey,
            };
        },

        /**
         * Exit fullscreen mode
         * Called when user closes fullscreen or presses ESC
         */
        exitFullscreen: (state) => {
            state.fullscreen = {
                isFullscreen: false,
                media: null,
                videoElementKey: null,
            };
        },
    },
    extraReducers: (builder) => {
        builder
            // Initialize video - pending
            .addCase(initVideo.pending, (state, action) => {
                const { media, videoKey, autoplay = false } = action.meta.arg;
                state.pendingInitialization[videoKey] = { media, autoplay };
            })
            // Initialize video - fulfilled
            .addCase(initVideo.fulfilled, (state, action) => {
                const { videoKey, autoplay } = action.payload;
                delete state.pendingInitialization[videoKey];

                // If autoplay is requested and video element is ready, play it
                if (autoplay) {
                    // Use setTimeout to ensure video element is registered
                    setTimeout(() => {
                        videoElementRegistry.play(videoKey);
                    }, 50);
                }
            })
            // Initialize video - rejected
            .addCase(initVideo.rejected, (state, action) => {
                const { videoKey } = action.meta.arg;
                delete state.pendingInitialization[videoKey];
            });
    },
});

export const {
    registerVideoElementState,
    unregisterVideoElementState,
    updatePlaybackState,
    setActiveVideo,
    clearActiveVideo,
    enterFullscreen,
    exitFullscreen,
    requestPlay,
    requestPause,
    clearAllVideoManagement,
} = videoManagementSlice.actions;

export default videoManagementSlice.reducer;
